// Long declarative text, a summary first (02-DESIGN/03-console/01-the-
// console.md § conventions: density and length): a rule's expression, a
// schema, a description drawn as its first lines with *Show more*, which
// opens the rest in place; and a list drawn as its first rows with *Show
// more* for the rest. A block taller than about twelve lines is never drawn
// whole when the page opens.

import { useState, type ReactNode } from "react";

import "./density.css";

/** TOP is how many rows a list shows before *Show more*: its top ten. */
export const TOP = 10;

/** clip is a text's first `lines` lines, and whether anything was left out. */
export function clip(text: string, lines: number): { shown: string; more: boolean } {
  const all = text.replace(/\s+$/, "").split("\n");
  if (all.length <= lines) {
    return { shown: all.join("\n"), more: false };
  }
  return { shown: all.slice(0, lines).join("\n"), more: true };
}

/** firstOf is a list's first `n` entries unless all are asked for, and how many it left out. */
export function firstOf<T>(rows: readonly T[], all: boolean, n = TOP): { shown: readonly T[]; left: number } {
  if (all || rows.length <= n) {
    return { shown: rows, left: 0 };
  }
  return { shown: rows.slice(0, n), left: rows.length - n };
}

/** MoreText draws a block of text as its first lines, with *Show more* and *Show less*. */
export function MoreText({
  text,
  lines = 1,
  mono = true,
}: {
  text: string;
  /** How many lines show before *Show more*: one where omitted. */
  lines?: number;
  mono?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const { shown, more } = clip(text, lines);
  return (
    <div className="more-text">
      <pre className={mono ? "more-block mono" : "more-block"}>{open ? text.replace(/\s+$/, "") : shown}</pre>
      {more && <ShowMore open={open} onToggle={setOpen} />}
    </div>
  );
}

/** ShowMore is the one *Show more* / *Show less* control, with the count beyond what shows where it has one. */
export function ShowMore({
  open,
  onToggle,
  left,
}: {
  open: boolean;
  onToggle: (open: boolean) => void;
  /** How many entries are beyond what shows. */
  left?: number;
}): ReactNode {
  return (
    <button
      type="button"
      className="text-link small show-more"
      aria-expanded={open}
      onClick={() => {
        onToggle(!open);
      }}
    >
      {open ? "Show less" : left !== undefined && left > 0 ? `Show more (${String(left)})` : "Show more"}
    </button>
  );
}
